import {isEmpty, trim} from 'lodash'

import {
  validateEmail,
  validatePhone,
  validateLength,
  validateDOB,
  validateCharacters,
  validatePartnerCode,
} from '../../../common/utils/validations'

/** Fields the client may leave blank without blocking the sign-up. */
export const OPTIONAL_FIELDS = ['promoCode']

/**
 * The anti-bot honeypot. It is hidden from humans, so it must never take part in client-side
 * validation: an error on it cannot be displayed or cleared, which silently dead-ended the
 * sign-up of every client whose password manager autofilled it (PT-5604). The server rejects
 * filled honeypots on its own, see `server/signup.ts`.
 */
export const HONEYPOT_FIELD = 'checkField'

/** Maximum combined length of first and last name. */
export const FULL_NAME_MAX_LENGTH = 32

type SignupForm = Record<string, any>

type ValidateSignupFieldsArgs = {
  form: SignupForm
  /** USA campaign clients are allowed to sign up without a phone number. */
  isUSACampaign?: boolean
  /** Whether the chosen password already failed the strength rules. */
  hasPasswordError?: boolean
}

type SignupFieldValidation = {
  /** `true` marks an invalid field. */
  errors: Record<string, boolean>
  /**
   * Per-field outcome of the name checks: a message key when the name is malformed, otherwise
   * a boolean. Used by the form to pick the exact error text to display.
   */
  errorsKeys: Record<string, string | boolean>
}

/**
 * Validates the sign-up form field by field.
 *
 * Pure on purpose: this is the logic that decides whether Submit does anything at all, so it
 * has to be testable without mounting the whole registration screen.
 */
export const validateSignupFields = ({
  form,
  isUSACampaign,
  hasPasswordError,
}: ValidateSignupFieldsArgs): SignupFieldValidation => {
  const errors: Record<string, boolean> = {}
  const errorsKeys: Record<string, string | boolean> = {}

  for (const field of Object.keys(form)) {
    if (field === 'phone') {
      errors[field] = isUSACampaign
        ? false
        : isEmpty(form[field]) || !validatePhone(form[field])
    } else if (field === 'email') {
      errors[field] = isEmpty(form[field]) || !validateEmail(form[field])
    } else if (field === 'website') {
      errors[field] = isEmpty(form[field])
      errors['websiteLength'] = !validateLength(form[field], 150)
    } else if (field === 'birthday') {
      errors[field] = !validateDOB(form[field])
    } else if (field === 'firstName' || field === 'lastName') {
      const nameError = validateCharacters(trim(form[field]))

      errorsKeys[field] = nameError
      errors[field] = !!nameError
    } else if (field === 'partnerReferenceNumber') {
      errors[field] = validatePartnerCode(trim(form[field]))
    } else if (field !== HONEYPOT_FIELD) {
      errors[field] = !(OPTIONAL_FIELDS.includes(field) || !isEmpty(form[field]))
    }
  }

  if ([form.firstName, form.lastName].join(' ').length > FULL_NAME_MAX_LENGTH) {
    errors.fullName = true
  }

  // A weak password used to abort submission without touching state at all, so the button
  // simply did nothing. Treat it like any other invalid field instead (PT-5604).
  if (hasPasswordError) {
    errors.password = true
  }

  return {errors, errorsKeys}
}
